import { FC, useEffect, useMemo, useState } from 'react';
import { IPopupWrapper } from '../../../../../../../../../shared/components/popup/components/popup.component.tsx';
import { Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { take } from 'rxjs';
import { fontHelper } from '../../../../../../../../../shared/helpers/font/font.helper.ts';
import {
  dictionaryAdditionalService,
  GetDictTextBlockRes,
} from '../../../../../../../../../shared/components/dictionary/services/dictionaryAdditional.service.ts';
import { Content as Wrapper } from '../../../../../../../../../shared/components/content/content.component.tsx';
import { FormProvider, useForm } from 'react-hook-form';
import { FormTextEditorWithButton } from '../../../../../../../../../shared/components/form';
import { dictionaryState } from '../../../../../../../../../shared/components/dictionary/states/dictionary.state.ts';
import { responseHandler } from '../../../../../../../../../shared/responseHandler/responseHandler.ts';
import { useBeforeClosePopup } from '../../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import TranslateIcon from '@mui/icons-material/Translate';
import { TranslationPopup } from '../../../../../../../../../shared/components/popup/components/translation/translation.popup.tsx';
import { C_Content_Translation_Type } from '../../../../../../../../../graphql/generatedModel.ts';
import {
  configsData,
  DictTranslationLanguagesQueryRes,
} from '../../../../../../../../../shared/services/configsData/configsData.service.ts';

export const ContentPopup: FC<IContent> = ({ stream }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [id, setId] = useState<string>('');
  const [translationLanguages, setTranslationLanguages] =
    useState<DictTranslationLanguagesQueryRes>([]);
  const defaultFontSize = useMemo(() => fontHelper.getDefaultFont(['fontSize']), []);
  const translationPopup = useMemo(() => new TranslationPopup(), []);
  const formMethods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      textBlock: '',
      textBlockTranslations: [] as GetDictTextBlockRes['textBlockTranslations'],
    },
  });

  const {
    formState: { isDirty },
    setValue,
    getValues,
  } = formMethods;

  useEffect(() => {
    const langSub = configsData.sub
      .dictTranslationLanguages()
      .pipe(take(1))
      .subscribe((langs) => {
        setTranslationLanguages(langs || []);
      });
    configsData.pub.common(['dictTranslationLanguages']);

    const dictionaryAdditionalSub = dictionaryAdditionalService.sub
      .getDictTextBlock()
      .pipe(
        responseHandler<GetDictTextBlockRes>({
          errorReturnType: { textBlock: '', textBlockTranslations: [] } as GetDictTextBlockRes,
        }),
      )
      .subscribe((data) => {
        formMethods.reset({
          textBlock: data?.textBlock || '',
          textBlockTranslations: data?.textBlockTranslations || [],
        });
        setLoading(false);
      });

    stream
      .actionListener('open')
      .pipe(take(1))
      .subscribe(({ id }) => {
        dictionaryAdditionalService.pub.getDictTextBlock({ id });
        setId(id);
      });

    const translationSub = translationPopup.stream.state().subscribe((v) => {
      if (v?.action === 'reset') {
        setValue('textBlockTranslations', v?.values?.translations || []);
      }
      if (v?.action === 'recordData') {
        setValue('textBlockTranslations', v?.values?.translations || [], { shouldDirty: true });
      }
    });

    return () => {
      dictionaryAdditionalSub.unsubscribe();
      translationSub.unsubscribe();
      translationPopup.stream.unsubscribe();
      langSub.unsubscribe();
    };
  }, []);

  useBeforeClosePopup({
    stream,
    isDirty,
  });

  const onSubmit = ({
    textBlock,
    textBlockTranslations,
  }: {
    textBlock: string | null;
    textBlockTranslations: GetDictTextBlockRes['textBlockTranslations'];
  }) => {
    const defaultValue = formMethods.formState.defaultValues?.textBlock || '';
    const defaultTranslations = formMethods.formState.defaultValues?.textBlockTranslations || [];
    const textBlockChanged = defaultValue !== textBlock;
    const translationsChanged =
      JSON.stringify(defaultTranslations) !== JSON.stringify(textBlockTranslations);

    if (textBlockChanged || translationsChanged) {
      dictionaryState.pub.updateCell(
        { id, field: 'textBlock', value: { textBlock, textBlockTranslations } },
        true,
      );
    }
    stream.emit('close');
  };

  return (
    <Wrapper type='box' {...{ loading }} boxProps={{ sx: { minHeight: '235px' } }}>
      <FormProvider {...formMethods}>
        <form
          style={{ opacity: loading ? '0' : '1', transition: '0.3s' }}
          onSubmit={formMethods.handleSubmit(onSubmit)}
          noValidate
        >
          {!loading && (
            <>
              <FormTextEditorWithButton
                name='textBlock'
                defaultFormat={defaultFontSize}
                buttonVisibility='hoverOrFocus'
                buttonIcon={<TranslateIcon />}
                buttonTooltip={t('common.translations')}
                onButtonClick={() =>
                  translationPopup.stream.emit('open', {
                    values: { translations: getValues().textBlockTranslations },
                    languages: translationLanguages,
                    contentType: C_Content_Translation_Type.TEXT_BLOCK_TEXT,
                    originalContent: getValues().textBlock ?? '',
                    isRichText: true,
                  })
                }
              />
              <Stack
                direction='row'
                spacing={2}
                sx={{
                  mt: 3,
                  justifyContent: 'flex-end',
                }}
              >
                <Button type='submit' variant='contained'>
                  {t('common.save')}
                </Button>
              </Stack>
            </>
          )}
        </form>
      </FormProvider>
      <translationPopup.Component />
    </Wrapper>
  );
};

interface IContent {
  stream: IPopupWrapper['stream'];
}
